/**
 * AI Interview Analytics Component
 *
 * Post-submission step tracking for the AI interview (speech-to-text)
 * inside Funnel Insights. Strictly separate from the pre-submission
 * funnel tracking: it reads its own endpoint
 * (`funnel-insight/:id/interview`, based on LeadResponse.interview)
 * and never touches the FunnelInsight event data.
 *
 * The card is independent of the funnel version (the interview is not
 * part of funnelQA versions) and hides itself completely when the
 * funnel has no interview data.
 *
 * @module components/analytics/ai-interview-analytics
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Loader from "../loader/loader";
import { CustomCard } from "../common-styling";
import { fetchInterviewInsight } from "../../services/api-declaration";
import { DATE_RANGE_MODE, findRangeMode } from "./analytics-helper";

const BAR_COLOR = {
  BASE: "#adb5bd",
  SHOWN: "#6ea8fe",
  STEP: "#0b8ffb",
  COMPLETED: "#20c997"
};
const CHART_HEIGHT = 140;

const StatTile = ({ title, value, subtext }) => (
  <div className="flex-fill p-3 border rounded bg-white">
    <div className="text-secondary" style={{ fontSize: 13 }}>
      {title}
    </div>
    <h4 className="mb-0 fw-bold">{value}</h4>
    {subtext ? (
      <div className="text-secondary" style={{ fontSize: 12 }}>
        {subtext}
      </div>
    ) : null}
  </div>
);

const AIInterviewAnalytics = ({
  funnelId,
  currentDateRangeMode,
  dateRange
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // remembers whether this funnel ever returned interview data, so an
  // empty date range shows zeros instead of hiding the whole card
  const hadDataRef = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // mirrors the date normalization of fetchFunnelInsightsData
      let dateRangeMode = currentDateRangeMode;
      let startDate = dateRange[0];
      let endDate = new Date(dateRange[1]);
      endDate.setDate(endDate.getDate() + 1);
      if (dateRangeMode === DATE_RANGE_MODE.RANGE) {
        dateRangeMode = findRangeMode({ startDate, endDate });
      }
      if (currentDateRangeMode === DATE_RANGE_MODE.ALL_TIME) {
        startDate = endDate = undefined;
      }
      const response = await fetchInterviewInsight(funnelId, {
        startDate,
        endDate,
        dateRangeMode
      });
      if (response.data.data?.shown) {
        hadDataRef.current = true;
      }
      setData(response.data.data);
    } catch (error) {
      console.error("Error fetching interview insights: ", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [funnelId, currentDateRangeMode, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // feature not used on this funnel at all: no card. An empty date range
  // on a funnel that has interview data keeps the card (with zeros).
  if (!loading && !data?.shown && !hadDataRef.current) {
    return null;
  }

  const percentOf = (count, base) =>
    base ? `${Math.round((count / base) * 100)}%` : "0%";

  const bars = data
    ? [
        {
          key: "leadsTotal",
          label: t("funnelInsights.interview.leadsTotal"),
          count: data.leadsTotal,
          percent: null,
          color: BAR_COLOR.BASE
        },
        {
          key: "shown",
          label: t("funnelInsights.interview.shown"),
          count: data.shown,
          percent: percentOf(data.shown, data.leadsTotal),
          color: BAR_COLOR.SHOWN
        },
        ...data.steps.map((step) => ({
          key: `step-${step.step}`,
          label: `${t("funnelInsights.interview.stepShort")}${step.step}`,
          count: step.count,
          percent: percentOf(step.count, data.shown),
          color: BAR_COLOR.STEP
        })),
        {
          key: "completed",
          label: t("funnelInsights.interview.completed"),
          count: data.completed,
          percent: percentOf(data.completed, data.shown),
          color: BAR_COLOR.COMPLETED
        }
      ]
    : [];
  const maxCount = Math.max(...bars.map((bar) => bar.count), 1);
  const answersTotal = data
    ? data.inputModes.voice + data.inputModes.chip + data.inputModes.text
    : 0;

  return (
    <CustomCard className="my-4">
      <Card.Header className="bg-white kpi-tiles-header">
        <h5 className="mb-0 fw-bold">{t("funnelInsights.interview.title")}</h5>
        <div className="kpi-date">
          ( {t("funnelInsights.interview.versionIndependent")}
          {data?.activeSince
            ? ` · ${t("funnelInsights.interview.sinceActivation", {
                date: moment(data.activeSince).format("DD.MM.YYYY")
              })}`
            : ""}{" "}
          )
        </div>
      </Card.Header>
      {loading ? (
        <Loader />
      ) : (
        <div className="p-4">
          <div
            className="d-flex align-items-end justify-content-around"
            style={{ height: CHART_HEIGHT + 50 }}
          >
            {bars.map((bar) => (
              <div
                key={bar.key}
                className="d-flex flex-column align-items-center justify-content-end h-100"
                style={{ minWidth: 60 }}
              >
                <div className="fw-bold" style={{ fontSize: 13 }}>
                  {bar.count}
                </div>
                <div
                  style={{
                    width: 38,
                    borderRadius: "4px 4px 0 0",
                    backgroundColor: bar.color,
                    height: Math.max(
                      Math.round((bar.count / maxCount) * CHART_HEIGHT),
                      2
                    )
                  }}
                />
                <div style={{ fontSize: 12 }}>{bar.label}</div>
                <div className="text-secondary" style={{ fontSize: 11 }}>
                  {bar.percent || " "}
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex flex-wrap gap-3 mt-4">
            <StatTile
              title={t("funnelInsights.interview.startRate")}
              value={percentOf(data.started, data.shown)}
              subtext={t("funnelInsights.interview.startRateSub")}
            />
            <StatTile
              title={t("funnelInsights.interview.completionRate")}
              value={percentOf(data.completed, data.shown)}
              subtext={t("funnelInsights.interview.completionRateSub")}
            />
            <StatTile
              title={t("funnelInsights.interview.avgAnswered")}
              value={data.avgAnswered}
              subtext={`${t("funnelInsights.interview.avgPlanned")} ${
                data.avgPlanned
              }`}
            />
            <StatTile
              title={t("funnelInsights.interview.voiceShare")}
              value={percentOf(data.inputModes.voice, answersTotal)}
              subtext={`${t("funnelInsights.interview.chips")} ${percentOf(
                data.inputModes.chip,
                answersTotal
              )} · ${t("funnelInsights.interview.text")} ${percentOf(
                data.inputModes.text,
                answersTotal
              )}`}
            />
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3">
            <StatTile
              title={t("funnelInsights.interview.skippedImmediately")}
              value={data.skippedImmediately}
            />
            <StatTile
              title={t("funnelInsights.interview.skippedPartial")}
              value={data.skippedPartial}
            />
            <StatTile
              title={t("funnelInsights.interview.abandoned")}
              value={data.abandoned}
            />
          </div>
        </div>
      )}
    </CustomCard>
  );
};

export default AIInterviewAnalytics;
